// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/** Domain jargon, defined for a reader who knows nothing about name statistics. */
export interface GlossaryEntry {
  term: string;
  title: string;
  body: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  registered: {
    term: 'registered',
    title: 'Registered births',
    body:
      'The figures count babies whose birth was registered with the state Registry of Births, Deaths & Marriages in that calendar year — with the exact first name the parents recorded. Almost every birth is registered, so this is very close to a full count, but the year is the registration year, which can lag the actual birth by a few months.',
  },
  rank: {
    term: 'rank',
    title: 'Rank',
    body:
      'A name\'s position for its sex and state in a given year: rank 1 is the most-registered name that year, rank 2 the next, and so on down to 100. Each state publishes only its top 100 boys\' and top 100 girls\' names each year, so a name with no rank in a year simply fell outside that top 100 — not necessarily to zero.',
  },
  count: {
    term: 'count',
    title: 'Count (number of babies)',
    body:
      'How many babies were registered with exactly that spelling of the name, for that sex and state, in that year. Spellings are counted separately — "Mia" and "Mya" are different names here — which is one reason no single name dominates the way it once did.',
  },
  top100: {
    term: 'top100',
    title: 'Why only 100 names?',
    body:
      'The registries release the 100 most popular boys\' names and 100 most popular girls\' names for each year — not the full long tail. So this site can show the popular end in great depth over decades, but it cannot see rare names, and a name "disappearing" from a chart means it left the top 100, not that no baby was given it.',
  },
  concentration: {
    term: 'concentration',
    title: 'Concentration (how much the top names dominate)',
    body:
      'The share of all top-100 babies who got one of the ten most popular names. When a handful of names covered most babies, concentration was high; as parents spread across many more names, it falls. It is the clearest single measure of how naming has fragmented — and it has fallen steadily for both boys and girls.',
  },
  unisex: {
    term: 'unisex',
    title: 'Unisex name',
    body:
      'A name given to both boys and girls often enough to appear in both top-100 lists. Some are close to 50/50 (truly shared); others lean heavily one way and drift over the decades — a name can start out mostly for boys and end up mostly for girls, or the reverse.',
  },
  coverage: {
    term: 'coverage',
    title: 'Which states are covered',
    body:
      'This site uses New South Wales (1952 onward) and Queensland (1960 onward) — the two states that publish long, open, count-level historical name data. Victoria, WA, SA, Tasmania, the ACT and the NT publish only short or rank-only lists, so they are not included. Together NSW and QLD are close to half of all Australian births, and the fashions they show are national.',
  },
  registration_year: {
    term: 'registration_year',
    title: 'Registration year, not birth year',
    body:
      'Names are grouped by the year the birth was registered. A baby born in late December is often registered the following year, so counts reflect registrations rather than an exact birth-year census. Over a full year the difference is small.',
  },
};

export function glossaryTerm(term: string, label?: string): string {
  const entry = GLOSSARY[term];
  const text = label ?? entry?.title ?? term;
  return `<span class="glossary-link" data-term="${term}" tabindex="0" role="button">${text}</span>`;
}

export function infoIcon(term: string): string {
  return `<button class="info-icon" data-term="${term}" aria-label="What does this mean?" type="button">i</button>`;
}
